/**
 * UsageTracker — mounts once inside the router and feeds route changes to the
 * usage capture (src/lib/usage/capture.ts). The capture module itself is
 * imported lazily after first paint, so it is never on the critical path and
 * never in the entry chunk.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type Capture = typeof import('./capture');

let loading: Promise<Capture> | null = null;
const capture = (): Promise<Capture> => (loading ??= import('./capture'));

/** Breadcrumbs for a bug report — [] until the capture has loaded. */
export async function usageBreadcrumbs(): Promise<{ breadcrumbs: ReturnType<Capture['getBreadcrumbs']>; routePattern: string }> {
  const m = await capture();
  return { breadcrumbs: m.getBreadcrumbs(), routePattern: m.currentScreen() };
}

export function UsageTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      capture()
        .then((m) => {
          if (cancelled) return;
          m.startUsageCapture();
          m.trackScreen(window.location.pathname);
        })
        .catch(() => {
          /* analytics never break the app */
        });
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(start, { timeout: 3000 });
    else setTimeout(start, 1500);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loading) return; // the first screen is tracked when the capture starts
    capture()
      .then((m) => m.trackScreen(pathname))
      .catch(() => {
        /* analytics never break the app */
      });
  }, [pathname]);

  return null;
}
